// src/pages/CompetitionJudokaRegistrationPage/index.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaIdCard,
  FaMapMarkerAlt,
  FaRedoAlt,
  FaTrophy,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "../../hooks/ScrollIndicator";
import Button from "../../components/Button/Button";

import { addCompetiteur } from "../../services/competiteursServices";
import { getCompetitionById } from "../../services/competitionsServices";

const ALL_DISCIPLINES = [
  "Judo",
  "Ne-Waza",
  "JJB",
  "Jujitsu Combat",
  "Jujitsu Duo",
  "Arbitre",
  "Commissaire Sportif",
  "Judo Adapté"
];

const GRADE_OPTIONS = [
  { label: "Ceinture blanche", value: "blanche" },
  { label: "Ceinture blanche 1 liseré", value: "blanche 1 liseré" },
  { label: "Ceinture blanche 2 liserés", value: "blanche 2 liserés" },
  { label: "Ceinture blanche-jaune", value: "blanche-jaune" },
  { label: "Ceinture jaune", value: "jaune" },
  { label: "Ceinture jaune-orange", value: "jaune-orange" },
  { label: "Ceinture orange", value: "orange" },
  { label: "Ceinture orange-verte", value: "orange-verte" },
  { label: "Ceinture verte", value: "verte" },
  { label: "Ceinture verte-bleue", value: "verte-bleue" },
  { label: "Ceinture bleue", value: "bleue" },
  { label: "Ceinture bleue-marron", value: "bleue-marron" },
  { label: "Ceinture marron", value: "marron" },
  { label: "1er Dan", value: "1er Dan" },
  { label: "2e Dan", value: "2e Dan" },
  { label: "3e Dan", value: "3e Dan" },
  { label: "4e Dan", value: "4e Dan" },
  { label: "5e Dan", value: "5e Dan" },
];

const normalize = (str) =>
  String(str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

const getGradeClasses = (g = "") => {
  const s = normalize(g);

  if (s.includes("blanche-jaune")) {
    return "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  }

  if (s.includes("blanche")) {
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  }

  if (s.includes("jaune")) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
  }

  if (s.includes("orange")) {
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
  }

  if (s.includes("verte")) {
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
  }

  if (s.includes("bleue")) {
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
  }

  if (s.includes("marron") || s.includes("brun")) {
    return "bg-amber-900 text-amber-100 dark:bg-amber-900/40 dark:text-amber-100";
  }

  if (s.includes("dan")) {
    return "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900";
  }

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const coerceGradeValue = (raw = "") => {
  if (!raw) return "";

  const trimmed = String(raw).trim();

  if (/^ceinture\s+/i.test(trimmed)) {
    return trimmed.replace(/^ceinture\s+/i, "");
  }

  return trimmed;
};

const labelFromValue = (value) => {
  const v = String(value ?? "").trim();

  if (!v) return "Aucun grade";

  return GRADE_OPTIONS.find((o) => o.value === v)?.label || v;
};

const normalizeLicence = (value = "") =>
  String(value ?? "")
    .toUpperCase()
    .replace(/\s+/g, "");

const parseWeightOrNull = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return null;

  const cleaned = String(raw).trim().replace(",", ".");

  if (!cleaned) return null;

  const weight = Number(cleaned);

  if (!Number.isFinite(weight)) return Number.NaN;

  return weight;
};

const formatWeight = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Non renseigné";
  }

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "Non renseigné";
  }

  return `${numberValue.toFixed(1)} kg`;
};

const emptyForm = {
  nom_competiteur: "",
  prenom_competiteur: "",
  ddn_competiteur: "",
  licence_competiteur: "",
  genre_competiteur: "M",
  poids_competiteur: "",
  club_competiteur: "",
  discipline_competiteur: "Judo",
  grade_competiteur: "",
};

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-emerald-500 dark:focus:ring-emerald-950";

const labelClass =
  "mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200";

const helperClass = "mt-1 text-xs text-slate-500 dark:text-slate-400";

const InfoLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-3 py-3 text-sm dark:bg-slate-900/60">
    <Icon className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300" />
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="font-semibold text-slate-900 dark:text-white">
        {value || "—"}
      </p>
    </div>
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 text-sm last:border-b-0 dark:border-slate-800">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="text-right font-semibold text-slate-900 dark:text-white">
      {value || "—"}
    </span>
  </div>
);

const formatBirthDateInput = (value = "") => {
  const digits = String(value).replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseFrenchDateToISO = (value = "") => {
  const raw = String(value).trim();

  const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (
    !Number.isInteger(day) ||
    !Number.isInteger(month) ||
    !Number.isInteger(year)
  ) {
    return null;
  }

  if (year < 1900 || year > new Date().getFullYear()) {
    return null;
  }

  if (month < 1 || month > 12) {
    return null;
  }

  if (day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  const isValidDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  if (!isValidDate) return null;

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0",
  )}`;
};

const formatDateForDisplay = (value) => {
  if (!value) return "—";

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    return value;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
};

const CompetitionJudokaRegistrationPage = () => {
  useScrollToTop();

  const { competitionId } = useParams();

  const [competition, setCompetition] = useState(null);
  const [availableDisciplines, setAvailableDisciplines] = useState(["Judo"]);
  const [form, setForm] = useState(emptyForm);
  const [lastRegistered, setLastRegistered] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        setLoading(true);

        const data = await getCompetitionById(competitionId);
        setCompetition(data);

        let parsed = ["Judo"];

        try {
          parsed = Array.isArray(data.disciplines_competition)
            ? data.disciplines_competition
            : JSON.parse(data.disciplines_competition || '["Judo"]');
        } catch {
          parsed = ["Judo"];
        }

        const finalList = parsed.filter((d) => ALL_DISCIPLINES.includes(d));
        const disciplines = finalList.length ? finalList : ["Judo"];

        setAvailableDisciplines(disciplines);

        setForm((prev) => ({
          ...prev,
          discipline_competiteur: disciplines[0],
        }));
      } catch {
        toast.error("Erreur lors du chargement de la compétition.");
      } finally {
        setLoading(false);
      }
    };

    if (competitionId) fetchCompetition();
  }, [competitionId]);

  const competitionDate = useMemo(() => {
    if (!competition?.date_competition) return "Date non renseignée";

    return new Date(competition.date_competition).toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }, [competition]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === "licence_competiteur") {
      nextValue = normalizeLicence(value);
    }

    if (name === "ddn_competiteur") {
      nextValue = formatBirthDateInput(value);
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const validateForm = () => {
    if (!form.nom_competiteur.trim()) {
      toast.error("Le nom est requis.");
      return false;
    }

    if (!form.prenom_competiteur.trim()) {
      toast.error("Le prénom est requis.");
      return false;
    }

    if (!form.ddn_competiteur) {
      toast.error("La date de naissance est requise.");
      return false;
    }

    if (!parseFrenchDateToISO(form.ddn_competiteur)) {
      toast.error("Date de naissance invalide. Format attendu : jj/mm/aaaa.");
      return false;
    }

    if (!form.club_competiteur.trim()) {
      toast.error("Le club est requis.");
      return false;
    }

    if (!form.genre_competiteur) {
      toast.error("Le genre est requis.");
      return false;
    }

    if (!form.discipline_competiteur) {
      toast.error("La discipline est requise.");
      return false;
    }

    const poids = parseWeightOrNull(form.poids_competiteur);

    if (Number.isNaN(poids) || (poids !== null && (poids < 0 || poids > 300))) {
      toast.error("Le poids est invalide. Exemple accepté : 40,1 ou 40.1");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const poids = parseWeightOrNull(form.poids_competiteur);
    const birthDateISO = parseFrenchDateToISO(form.ddn_competiteur);

    const payload = {
      nom_competiteur: form.nom_competiteur.trim(),
      prenom_competiteur: form.prenom_competiteur.trim(),
      ddn_competiteur: birthDateISO,
      licence_competiteur: normalizeLicence(form.licence_competiteur),
      genre_competiteur: form.genre_competiteur,
      poids_competiteur: poids,
      club_competiteur: form.club_competiteur.trim(),
      discipline_competiteur: form.discipline_competiteur,
      grade_competiteur: coerceGradeValue(form.grade_competiteur) || null,
      competition_id: competitionId,
    };

    try {
      setSubmitting(true);

      await addCompetiteur(payload);

      setLastRegistered(payload);

      toast.success("Inscription enregistrée.");

      setForm((prev) => ({
        ...emptyForm,
        club_competiteur: prev.club_competiteur,
        genre_competiteur: prev.genre_competiteur || "M",
        discipline_competiteur:
          prev.discipline_competiteur || availableDisciplines[0] || "Judo",
      }));

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de l'inscription.";

      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterAnotherSameClub = () => {
    setLastRegistered(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRegisterAnotherClub = () => {
    setLastRegistered(null);

    setForm((prev) => ({
      ...prev,
      club_competiteur: "",
    }));

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 pt-24 text-slate-700 dark:bg-slate-950 dark:text-slate-200">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement de la compétition...
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 pt-24 text-slate-700 dark:bg-slate-950 dark:text-slate-200">
        <div className="rounded-3xl border border-red-200 bg-white p-6 text-center shadow-sm dark:border-red-900/60 dark:bg-slate-900">
          <p className="font-bold text-red-700 dark:text-red-300">
            Compétition introuvable.
          </p>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Le QR code est peut-être incorrect ou la compétition n’existe plus.
          </p>
        </div>

        <ToastContainer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-3 pb-8 pt-[110px] dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <main className="mx-auto w-full max-w-xl">
        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative bg-slate-950 px-5 py-6 text-white">
            <div className="absolute pt-10 inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.35),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.25),transparent_36%)]" />

            <div className="relative">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-100 ring-1 ring-white/15">
                <FaUserPlus />
                Inscription sur place
              </p>

              <h1 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl">
                {competition.nom_competition || "Compétition"}
              </h1>

              <p className="mt-2 text-sm text-slate-200">
                Remplissez le formulaire pour enregistrer votre inscription à
                l’accueil du tournoi.
              </p>
            </div>
          </div>

          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <InfoLine
              icon={FaCalendarAlt}
              label="Date"
              value={competitionDate}
            />

            <InfoLine
              icon={FaMapMarkerAlt}
              label="Lieu"
              value={competition.lieu_competition || "Lieu non renseigné"}
            />

            <InfoLine
              icon={FaTrophy}
              label="Niveau"
              value={competition.level_competition || "Non renseigné"}
            />

            <InfoLine
              icon={FaUsers}
              label="Disciplines"
              value={availableDisciplines.join(", ")}
            />
          </div>
        </section>

        {lastRegistered ? (
          <section className="mt-5 rounded-[28px] border border-emerald-200 bg-white p-5 shadow-sm dark:border-emerald-900/60 dark:bg-slate-900">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                <FaCheckCircle className="text-2xl" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Inscription enregistrée
                </h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Présentez-vous à la table d’accueil si une vérification est
                  nécessaire.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl bg-slate-50 p-4 dark:bg-slate-950/60">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <FaIdCard />
                Résumé de l’inscription
              </h3>

              <SummaryRow label="Nom" value={lastRegistered.nom_competiteur} />
              <SummaryRow
                label="Prénom"
                value={lastRegistered.prenom_competiteur}
              />
              <SummaryRow
                label="Date de naissance"
                value={formatDateForDisplay(lastRegistered.ddn_competiteur)}
              />
              <SummaryRow
                label="Licence"
                value={lastRegistered.licence_competiteur || "Non renseignée"}
              />
              <SummaryRow
                label="Genre"
                value={
                  lastRegistered.genre_competiteur === "F"
                    ? "Féminin"
                    : "Masculin"
                }
              />
              <SummaryRow
                label="Poids"
                value={formatWeight(lastRegistered.poids_competiteur)}
              />
              <SummaryRow
                label="Discipline"
                value={lastRegistered.discipline_competiteur}
              />
              <SummaryRow
                label="Grade"
                value={labelFromValue(lastRegistered.grade_competiteur)}
              />
              <SummaryRow
                label="Club"
                value={lastRegistered.club_competiteur}
              />
            </div>

            <div className="mt-5 grid gap-3">
              <Button
                variant="brand"
                size="md"
                fullWidth
                className="rounded-2xl"
                icon={FaRedoAlt}
                onClick={handleRegisterAnotherSameClub}
              >
                Inscrire un autre judoka du même club
              </Button>

              <Button
                variant="outline"
                size="md"
                fullWidth
                className="rounded-2xl"
                onClick={handleRegisterAnotherClub}
              >
                Inscrire un judoka d’un autre club
              </Button>
            </div>
          </section>
        ) : (
          <section className="mt-5 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-5">
            <div className="mb-5">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Formulaire d’inscription
              </h2>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Les champs avec * sont obligatoires.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelClass}>Nom *</label>
                <input
                  name="nom_competiteur"
                  value={form.nom_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex : Dupont"
                  autoComplete="family-name"
                />
              </div>

              <div>
                <label className={labelClass}>Prénom *</label>
                <input
                  name="prenom_competiteur"
                  value={form.prenom_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex : Lucas"
                  autoComplete="given-name"
                />
              </div>

              <div>
                <label className={labelClass}>Date de naissance *</label>
                <input
                  type="text"
                  name="ddn_competiteur"
                  value={form.ddn_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="jj/mm/aaaa"
                  inputMode="numeric"
                  autoComplete="bday"
                  maxLength={10}
                />
                <p className={helperClass}>Exemple : 14/03/2012.</p>
              </div>

              <div>
                <label className={labelClass}>Club *</label>
                <input
                  name="club_competiteur"
                  value={form.club_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex : Judo Club du Bouscat"
                  autoComplete="organization"
                />
                <p className={helperClass}>
                  Renseignez le nom exact de votre club.
                </p>
              </div>

              <div>
                <label className={labelClass}>Licence</label>
                <input
                  type="text"
                  name="licence_competiteur"
                  value={form.licence_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex : M10071971VICTO01"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                />
                <p className={helperClass}>
                  Exemple de format : M10071971VICTO01.
                </p>
              </div>

              <div>
                <label className={labelClass}>Genre *</label>
                <select
                  name="genre_competiteur"
                  value={form.genre_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Poids</label>
                <input
                  type="text"
                  name="poids_competiteur"
                  value={form.poids_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex : 40,1 ou 40.1"
                  inputMode="decimal"
                  autoComplete="off"
                />
                <p className={helperClass}>
                  Facultatif. Les formats 40,1 et 40.1 sont acceptés.
                </p>
              </div>

              <div>
                <label className={labelClass}>Discipline *</label>
                <select
                  name="discipline_competiteur"
                  value={form.discipline_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="" disabled>
                    — Choisir une discipline —
                  </option>

                  {(availableDisciplines.length
                    ? availableDisciplines
                    : ["Judo"]
                  ).map((discipline) => (
                    <option key={discipline} value={discipline}>
                      {discipline}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Grade</label>
                <select
                  name="grade_competiteur"
                  value={form.grade_competiteur}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">— Aucun grade renseigné —</option>
                  {GRADE_OPTIONS.map((grade) => (
                    <option key={grade.value} value={grade.value}>
                      {grade.label}
                    </option>
                  ))}
                </select>

                <span
                  className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getGradeClasses(
                    form.grade_competiteur,
                  )}`}
                >
                  {labelFromValue(form.grade_competiteur)}
                </span>
              </div>

              <Button
                type="submit"
                variant="brand"
                size="lg"
                fullWidth
                className="rounded-2xl"
                icon={FaCheckCircle}
                disabled={submitting}
              >
                {submitting
                  ? "Inscription en cours..."
                  : "Valider l’inscription"}
              </Button>
            </form>
          </section>
        )}
      </main>

      <ToastContainer />
    </div>
  );
};

export default CompetitionJudokaRegistrationPage;
